import { HttpError, useStores } from '@littlebird/sdk';
import { useNavigate } from '@tanstack/react-router';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { FileGallery } from '@/components/blocks/FileGallery';
import { XStack, YStack } from '@/components/ui';
import { useAutoResizeTextarea } from '@/components/ui/form/useAutoResizeTextArea';
import { useChromelessSurface } from '@/hooks/useChromelessSurface';
import { logError } from '@/lib/errorLogging';
import { cn } from '@/lib/utils';

import { AgentAvatar } from './agents-avatar';
import { birdLookFromName } from './agents-bird.look';
import { AgentsLaunchRow } from './agents-launch-row';
import {
  type AgentEntry,
  agentsStore,
  LaunchNotStartedError,
  LAUNCH_DRAFT_ID,
  type LaunchRecord,
} from './agents.store';
import './agents.css';

const HEADING = 'What do you need done?';

const PLACEHOLDERS = [
  'Plan my trip to Lisbon',
  'Chase the three overdue invoices',
  'Prep me for Thursday’s board meeting',
  'Find every open PR older than a week',
  'Draft the Q4 hiring plan',
];

const PLACEHOLDER_INTERVAL_MS = 4000;

const VISIBLE_LAUNCHES = 3;
const HIDDEN_AVATARS = 3;

const isToday = (record: LaunchRecord) =>
  differenceInCalendarDays(new Date(), new Date(record.sentAt)) <= 0;

const launchErrorMessage = (error: unknown) =>
  error instanceof HttpError && error.userErrorMessage
    ? error.userErrorMessage
    : 'Could not start that. Try again.';

const useRotatingPlaceholder = (paused: boolean) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (paused) return;
    const timer = setInterval(
      () => setIndex((current) => (current + 1) % PLACEHOLDERS.length),
      PLACEHOLDER_INTERVAL_MS
    );
    return () => clearInterval(timer);
  }, [paused]);
  return PLACEHOLDERS[index];
};

export const AgentsLaunchPage = observer(() => {
  useChromelessSurface();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useAutoResizeTextarea<HTMLTextAreaElement>(query);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { drafts } = useStores();
  const files = drafts.getDraft(LAUNCH_DRAFT_ID)?.files ?? [];
  const isUploading = files.some(
    (file) => file.uploadState === 'pending' || file.uploadState === 'uploading'
  );
  const addFiles = (list: FileList | File[]) => {
    for (const file of Array.from(list)) {
      void drafts.addFileAndUpload(LAUNCH_DRAFT_ID, file);
    }
  };
  const placeholder = useRotatingPlaceholder(isFocused || query.length > 0);

  const openChat = (chatId: string) =>
    void navigate({ to: `/chats/${chatId}` });

  const submitted = useRef(false);
  const launch = () => {
    const text = query.trim();
    if (!text || isUploading || submitted.current) return;
    submitted.current = true;
    setQuery('');
    inputRef.current?.focus();
    agentsStore.launch(text).catch((error: unknown) => {
      logError(error, 'Failed to launch an agent', {
        context: 'AgentsLaunchPage',
      });
      toast.error(launchErrorMessage(error), { description: text });
      if (error instanceof LaunchNotStartedError) {
        submitted.current = false;
        setQuery((current) => current || text);
      }
    });
  };

  const undo = (record: LaunchRecord) => {
    agentsStore.undoRoutedSend().catch((error: unknown) => {
      logError(error, 'Failed to start a fresh agent on undo', {
        context: 'AgentsLaunchPage',
      });
      toast.error(launchErrorMessage(error), { description: record.query });
    });
  };

  const launches = agentsStore.launches;
  const initialLaunchIds = useRef(new Set(launches.map((record) => record.id)));
  const [showAll, setShowAll] = useState(false);
  const visibleLaunches = launches.slice(0, VISIBLE_LAUNCHES);
  const foldedLaunches = launches.slice(VISIBLE_LAUNCHES);
  const hiddenCount = foldedLaunches.length;
  const hiddenAgents = foldedLaunches
    .map((record) => record.agentId && agentsStore.getById(record.agentId))
    .filter((agent): agent is AgentEntry => Boolean(agent))
    .filter(
      (agent, index, all) =>
        all.findIndex((other) => other.agentId === agent.agentId) === index
    )
    .slice(0, HIDDEN_AVATARS);
  const launchRow = (record: LaunchRecord) => (
    <AgentsLaunchRow
      key={record.id}
      record={record}
      dimmed={!isToday(record)}
      entering={!initialLaunchIds.current.has(record.id)}
      onOpen={openChat}
      onUndo={() => undo(record)}
      onRetry={() =>
        agentsStore.retrySend(record.id).catch((error: unknown) => {
          logError(error, 'Failed to retry a launch', {
            context: 'AgentsLaunchPage',
          });
          toast.error(launchErrorMessage(error), { description: record.query });
        })
      }
    />
  );

  return (
    <YStack
      className={cn(
        'relative h-full min-w-0 flex-1 items-center overflow-y-auto px-6 pb-16 transition-[padding] duration-500 ease-out motion-reduce:transition-none',
        launches.length === 0 ? 'pt-[calc(50vh-11rem)]' : 'pt-[18vh]'
      )}
    >
      <YStack className="relative w-full max-w-3xl gap-5">
        <YStack className="items-center text-center">
          <span className="font-serif text-3xl tracking-tight text-foreground">
            {HEADING}
          </span>
        </YStack>
        <YStack
          className="gap-3 rounded-2xl border border-border-strong bg-surface px-5 pb-3.5 pt-5 shadow-[0_1px_2px_rgba(0,0,0,0.03),0_16px_40px_-16px_rgba(0,0,0,0.1)]"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            addFiles(event.dataTransfer.files);
          }}
        >
          {files.length > 0 && (
            <FileGallery
              attachedFiles={files}
              removeFile={(file) =>
                void drafts.removeFile(LAUNCH_DRAFT_ID, file)
              }
            />
          )}
          <textarea
            ref={inputRef}
            aria-label={HEADING}
            rows={2}
            value={query}
            placeholder={placeholder}
            autoFocus
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={(event) => {
              submitted.current = false;
              setQuery(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || event.shiftKey) return;
              event.preventDefault();
              launch();
            }}
            className="max-h-48 w-full resize-none bg-transparent text-base text-foreground outline-none placeholder:text-foreground-muted disabled:cursor-not-allowed"
          />
          <XStack className="items-center justify-between">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(event) => {
                if (event.target.files) addFiles(event.target.files);
                event.target.value = '';
              }}
            />
            <button
              type="button"
              aria-label="Attach files"
              onClick={() => fileInputRef.current?.click()}
              className="flex size-7 items-center justify-center rounded-full text-foreground-muted hover:bg-surface-hover hover:text-foreground focus-ring"
            >
              <Icons.paperclip className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Hand it off"
              disabled={!query.trim() || isUploading}
              onClick={launch}
              className={cn(
                'flex size-7 shrink-0 items-center justify-center rounded-full focus-ring',
                query.trim()
                  ? 'bg-accent text-accent-foreground hover:bg-accent-hover'
                  : 'bg-surface-active text-foreground-muted'
              )}
            >
              <Icons.send className="size-3.5" />
            </button>
          </XStack>
        </YStack>
        {launches.length === 0 && (
          <p className="mx-auto mt-10 max-w-xl text-center text-sm.5 leading-8 text-foreground-subtle">
            Describe a task and it goes to the right agent, like{' '}
            <ExampleAgent name="Travel Planner" />
            {' or '}
            <ExampleAgent name="Bookkeeper" />. If nobody owns it yet, a new one
            <span
              aria-hidden="true"
              className="mx-1.5 inline-block size-5 rounded-full border-[1.5px] border-dashed border-foreground-muted align-middle"
            />
            starts and names itself. Everything you hand off shows up here.
          </p>
        )}
        {launches.length > 0 && (
          <YStack className="mt-12 gap-9">
            {visibleLaunches.map(launchRow)}
            {hiddenCount > 0 && (
              <div
                className={cn(
                  '-mt-9 grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none',
                  showAll ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                )}
              >
                <YStack
                  className={cn(
                    '-mx-3 min-h-0 gap-9 overflow-hidden px-3 pt-9',
                    showAll && '-mb-2 pb-2'
                  )}
                  inert={!showAll}
                >
                  {foldedLaunches.map(launchRow)}
                </YStack>
              </div>
            )}
            {hiddenCount > 0 && (
              <span className="-mb-4 border-b border-border-subtle" />
            )}
            {hiddenCount > 0 && (
              <button
                type="button"
                aria-expanded={showAll}
                onClick={() => setShowAll((open) => !open)}
                className="group/more flex h-11 items-center gap-2.5 self-start rounded-lg pl-1 text-left focus-ring"
              >
                {!showAll && (
                  <XStack className="items-center">
                    {hiddenAgents.map((agent, index) => (
                      <AgentAvatar
                        key={agent.agentId}
                        bird={agent.bird}
                        className={cn('size-5', index > 0 && '-ml-1')}
                      />
                    ))}
                  </XStack>
                )}
                <span className="text-xs text-foreground-muted group-hover/more:text-foreground">
                  {showAll
                    ? 'Show fewer'
                    : hiddenCount === 1
                      ? '1 more task'
                      : `${hiddenCount} more tasks`}
                </span>
              </button>
            )}
          </YStack>
        )}
      </YStack>
    </YStack>
  );
});

const ExampleAgent = ({ name }: { name: string }) => (
  <span className="whitespace-nowrap font-medium text-foreground">
    <AgentAvatar
      bird={birdLookFromName(name)}
      className="mr-1.5 inline-block size-5 align-middle"
    />
    {name}
  </span>
);
